import type { RouteView } from '../game/Directions';
import { CELL, type CityMap } from '../game/map';

/**
 * Her phone's map app, shared by the on-screen phone and the real phone page.
 * The base image is static; only her marker is redrawn each frame.
 */
export function renderMapBase(map: CityMap, s: number): HTMLCanvasElement {
  const w = map.width * s;
  const h = map.height * s;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  ctx.fillStyle = '#1b2230';
  ctx.fillRect(0, 0, w, h);
  // Building blocks
  ctx.fillStyle = '#242d3d';
  for (let r = 0; r < map.height; r += 1) {
    for (let col = 0; col < map.width; col += 1) {
      if (!map.isStreet(col, r)) ctx.fillRect(col * s + 1.5, r * s + 1.5, s - 3, s - 3);
    }
  }
  for (let r = 0; r < map.height; r += 1) {
    for (let col = 0; col < map.width; col += 1) {
      if (!map.isStreet(col, r)) continue;
      const shrine = map.isShrine(col, r);
      ctx.fillStyle = shrine ? '#3c5a46' : '#8b97aa';
      const inset = shrine ? s * 0.34 : s * 0.22;
      ctx.fillRect(col * s + inset, r * s + inset, s - inset * 2, s - inset * 2);
      const conn = (dc: number, dr: number) =>
        map.isStreet(col + dc, r + dr) && (map.isShrine(col + dc, r + dr) === shrine || shrine);
      if (conn(1, 0)) ctx.fillRect(col * s + s / 2, r * s + inset, s / 2 + inset, s - inset * 2);
      if (conn(0, 1)) ctx.fillRect(col * s + inset, r * s + s / 2, s - inset * 2, s / 2 + inset);
    }
  }
  // Landmarks. The map app does not know about tonight's road works.
  const label = (text: string, col: number, r: number, color: string) => {
    ctx.fillStyle = color;
    ctx.font = `bold ${Math.round(s * 0.55)}px "Yu Gothic", "Meiryo", "Hiragino Sans", sans-serif`;
    ctx.fillText(text, col * s, r * s);
  };
  // Landmarks, if this night has them.
  for (const station of map.find('E')) label('夜野駅', station.c - 1.2, station.r + 0.75, '#c6d3e6');
  for (const konbini of map.find('C')) {
    ctx.fillStyle = '#4fbf8a';
    ctx.fillRect(konbini.c * s + s * 0.2, konbini.r * s + s * 0.2, s * 0.6, s * 0.6);
  }
  // Railway: a hatched line along the track row ('K' is the level crossing).
  for (let r = 0; r < map.height; r += 1) {
    for (let col = 0; col < map.width; col += 1) {
      const ch = map.char(col, r);
      if (ch !== 'r' && ch !== 'K') continue;
      ctx.fillStyle = '#5d6676';
      ctx.fillRect(col * s, r * s + s * 0.44, s, s * 0.12);
      ctx.fillStyle = '#c9cfd8';
      for (let k = 0; k < 3; k += 1) ctx.fillRect(col * s + (k + 0.3) * (s / 3), r * s + s * 0.36, s * 0.07, s * 0.28);
    }
  }
  for (const gate of map.find('G')) {
    ctx.fillStyle = '#d0452a';
    ctx.fillRect(gate.c * s + s * 0.3, gate.r * s + s * 0.15, s * 0.12, s * 0.7);
    ctx.fillRect(gate.c * s + s * 0.15, gate.r * s + s * 0.2, s * 0.45, s * 0.1);
  }

  // Home pin
  const [home] = map.find('H');
  if (!home) return c;
  const hx = (home.c + 0.5) * s;
  const hy = (home.r + 0.35) * s;
  ctx.fillStyle = '#ff5a5f';
  ctx.beginPath();
  ctx.arc(hx, hy, s * 0.42, Math.PI, 0);
  ctx.lineTo(hx, hy + s * 0.75);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(hx, hy, s * 0.16, 0, Math.PI * 2);
  ctx.fill();
  label('Home', home.c + 0.9, home.r + 0.7, '#ffd0d2');
  return c;
}

/**
 * Zoomed map that follows her: the base map is drawn at `s` px per cell, scrolled
 * so she sits a little below the middle. When home is off-screen an arrow at
 * the edge points the way.
 */
export function drawPhoneMap(
  ctx: CanvasRenderingContext2D,
  base: HTMLCanvasElement,
  s: number,
  x: number,
  z: number,
  heading: number,
  time: number,
  home?: { x: number; z: number },
  route?: RouteView | null,
): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const px = (x / CELL) * s;
  const py = (z / CELL) * s;
  const clampOffset = (v: number, size: number, view: number) =>
    size <= view ? (size - view) / 2 : Math.min(Math.max(v, 0), size - view);
  const ox = clampOffset(px - W / 2, base.width, W);
  const oy = clampOffset(py - H * 0.6, base.height, H);
  ctx.fillStyle = '#1b2230';
  ctx.fillRect(0, 0, W, H);
  // Copy only the visible window of the (large) base map.
  const sx = Math.max(0, ox);
  const sy = Math.max(0, oy);
  const sw = Math.min(W, base.width - sx);
  const sh = Math.min(H, base.height - sy);
  if (sw > 0 && sh > 0) ctx.drawImage(base, sx, sy, sw, sh, sx - ox, sy - oy, sw, sh);

  if (route) drawRoute(ctx, route, s, px - ox, py - oy, ox, oy);

  // Her position
  const mx = px - ox;
  const my = py - oy;
  const pulse = 0.5 + 0.5 * Math.sin(time * 4);
  ctx.fillStyle = `rgba(80,160,255,${0.18 + pulse * 0.15})`;
  ctx.beginPath();
  ctx.arc(mx, my, s * 0.9, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(mx, my);
  ctx.rotate(-heading);
  ctx.fillStyle = '#4ea2ff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, s * 0.08);
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.5);
  ctx.lineTo(s * 0.34, s * 0.34);
  ctx.lineTo(0, s * 0.18);
  ctx.lineTo(-s * 0.34, s * 0.34);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Home off-screen: an arrow at the edge.
  if (home) {
    const hx = (home.x / CELL) * s - ox;
    const hy = (home.z / CELL) * s - oy;
    const margin = s * 0.9;
    if (hx < 0 || hy < 0 || hx > W || hy > H) {
      const ax = Math.min(Math.max(hx, margin), W - margin);
      const ay = Math.min(Math.max(hy, margin), H - margin);
      const angle = Math.atan2(hy - ay, hx - ax);
      ctx.save();
      ctx.translate(ax, ay);
      ctx.fillStyle = 'rgba(15,18,26,0.85)';
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.62, 0, Math.PI * 2);
      ctx.fill();
      ctx.rotate(angle);
      ctx.fillStyle = '#ff5a5f';
      ctx.beginPath();
      ctx.moveTo(s * 0.42, 0);
      ctx.lineTo(-s * 0.22, -s * 0.28);
      ctx.lineTo(-s * 0.22, s * 0.28);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }
}

/** The way home: a line from her along the route's cell centres (dashed and faint on a weak signal), plus a status pill. */
function drawRoute(ctx: CanvasRenderingContext2D, route: RouteView, s: number, mx: number, my: number, ox: number, oy: number): void {
  const cells = route.cells;
  if (cells.length >= 4) {
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(mx, my);
    // Skip her own cell's centre so the line never doubles back behind her.
    for (let k = 2; k < cells.length; k += 2) ctx.lineTo((cells[k] + 0.5) * s - ox, (cells[k + 1] + 0.5) * s - oy);
    const weak = route.signal === 'weak';
    ctx.strokeStyle = weak ? 'rgba(13,27,54,0.55)' : '#0d1b36';
    ctx.lineWidth = s * 0.34;
    ctx.stroke();
    if (weak) {
      ctx.globalAlpha = 0.7;
      ctx.setLineDash([s * 0.3, s * 0.3]);
    }
    ctx.strokeStyle = weak ? '#7fb0ff' : '#3d8bff';
    ctx.lineWidth = s * 0.2;
    ctx.stroke();
    ctx.restore();
  }
  if (!route.note) return;
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  ctx.font = `600 ${Math.round(s * 0.4)}px system-ui, sans-serif`;
  const tw = ctx.measureText(route.note).width;
  const h = s * 0.7;
  const w = tw + s * 0.8;
  const x = (W - w) / 2;
  // Along the bottom, clear of the home arrow at the top edge.
  const y = H - h - s * 0.35;
  ctx.fillStyle = 'rgba(12,15,22,0.88)';
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, h / 2);
  ctx.fill();
  ctx.fillStyle = route.signal === 'none' ? '#ff8f8a' : '#ffd58a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(route.note, W / 2, y + h / 2 + 1);
  ctx.restore();
}
